import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { apiHandler } from '../../../api/apihandler';
import { LOGIN, LOGIN_OTP, RESEND_OTP } from '../../../api/constants';
import { setUserData } from '../../../store/setting/actions';
import { apiErrorMessage, apiSucceeded } from './onboarding/apiUtils';
import EyeIcon from './onboarding/EyeIcon';
import { getAuthenticatedDefaultPath } from '../../../utilities/teacherModulePermissions';
import VerifyStep from './onboarding/steps/VerifyStep';
import './onboarding/onboarding.css';

const RESEND_SECONDS = 60;

const SignIn = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [resendSeconds, setResendSeconds] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (step !== 2 || resendSeconds <= 0) return undefined;
    const timer = setInterval(() => {
      setResendSeconds((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [step, resendSeconds]);

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    setError('');

    const res = await apiHandler(LOGIN, 'POST', {
      identity: email.trim().toLowerCase(),
      password,
    });

    setSubmitting(false);

    if (!apiSucceeded(res)) {
      setError(apiErrorMessage(res, 'Invalid email or password.'));
      return;
    }

    const data = res?.data?.data || null;
    const token = data?.token?.token || (typeof data?.token === 'string' ? data.token : null);

    if (token) {
      completeLogin(data, token);
      return;
    }

    if (data?.user_id) {
      setUserId(data.user_id);
      setOtp(Array(6).fill(''));
      setResendSeconds(RESEND_SECONDS);
      setError('');
      setStep(2);
      return;
    }

    setError('Unexpected response from server. Please try again.');
  };

  const completeLogin = (data, token) => {
    localStorage.setItem('nemontoAuthToken', token);
    localStorage.setItem('user_email', email.trim().toLowerCase());
    const user = data?.user || data;
    dispatch(setUserData(JSON.stringify(user)));
    navigate(getAuthenticatedDefaultPath(user));
  };

  const handleVerifyOtp = async () => {
    setSubmitting(true);
    setError('');

    const res = await apiHandler(LOGIN_OTP, 'POST', {
      user_id: userId,
      otp: otp.join(''),
    });

    setSubmitting(false);

    if (!apiSucceeded(res)) {
      setError(apiErrorMessage(res, 'Invalid OTP.'));
      return;
    }

    const data = res?.data?.data || null;
    const token = data?.token?.token || (typeof data?.token === 'string' ? data.token : null);

    if (!token) {
      setError('Unexpected response from server. Please try again.');
      return;
    }

    completeLogin(data, token);
  };

  const handleResendOtp = async () => {
    setError('');
    const res = await apiHandler(RESEND_OTP, 'POST', {
      email: email.trim().toLowerCase(),
    });
    if (apiSucceeded(res)) {
      setOtp(Array(6).fill(''));
      setResendSeconds(RESEND_SECONDS);
    } else {
      setError(apiErrorMessage(res, 'Could not resend OTP.'));
    }
  };

  if (step === 2) {
    return (
      <div className="ob-root">
        <VerifyStep
          step={1}
          totalSteps={1}
          method="email"
          contact={{ email: email.trim().toLowerCase() }}
          otp={otp}
          setOtp={setOtp}
          resendSeconds={resendSeconds}
          onResend={handleResendOtp}
          onSubmit={handleVerifyOtp}
          onBack={() => setStep(1)}
          error={error}
          submitting={submitting}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #FAECE7 0%, #F5C4B3 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)'
      }}>
        <h1 style={{
          fontSize: '28px',
          marginBottom: '8px',
          color: '#2C2C2A',
          fontFamily: "'DM Serif Display', serif"
        }}>Sign In</h1>
        <p style={{
          fontSize: '14px',
          color: '#666',
          marginBottom: '24px'
        }}>Welcome back to Nemonto</p>

        {error && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSignIn}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '6px',
              color: '#2C2C2A'
            }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '14px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                boxSizing: 'border-box',
                fontFamily: "'DM Sans', sans-serif"
              }}
              placeholder="your@email.com"
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '6px',
              color: '#2C2C2A'
            }}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  paddingRight: '40px',
                  fontSize: '14px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  boxSizing: 'border-box',
                  fontFamily: "'DM Sans', sans-serif"
                }}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666',
                  cursor: 'pointer'
                }}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
            <Link to="/auth/forgot-password" style={{
              display: 'block',
              textAlign: 'right',
              marginTop: '8px',
              fontSize: '13px',
              color: '#D85A30',
              textDecoration: 'none',
              fontWeight: '600'
            }}>Forgot password?</Link>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '14px',
              background: submitting ? '#e39a7d' : '#D85A30',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => { if (!submitting) e.target.style.background = '#c74820'; }}
            onMouseOut={(e) => { if (!submitting) e.target.style.background = '#D85A30'; }}
          >
            {submitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p style={{
          marginTop: '20px',
          textAlign: 'center',
          fontSize: '14px',
          color: '#666'
        }}>
          Don't have an account? <Link to="/auth/sign-up" style={{
            color: '#D85A30',
            textDecoration: 'none',
            fontWeight: '600'
          }}>Sign up</Link>
        </p>
      </div>
    </div>
  );
};

export default SignIn;
